import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import TeamArchiveModal from "../../../../../components/dashboardcomponents/People/Teams/TeamArchiveModal";
import TeamDetailHeaderBand from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/TeamDetailHeaderBand";
import TeamDetailStatsRow from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/TeamDetailStatsRow";
import TeamDetailInfoSection from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/TeamDetailInfoSection";
import TeamDetailGroupsSection from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/TeamDetailGroupsSection";
import TeamDetailChannelsSection from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/TeamDetailChannelsSection";
import TeamDetailMembersSection from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/TeamDetailMembersSection";
import { buildTeamDetailView } from "../../../../../components/dashboardcomponents/People/Teams/TeamDetail/teamDetailHelpers";
import {
  TEAM_DETAIL_ARCHIVE,
  TEAM_DETAIL_EDIT,
} from "../../../../../components/dashboardcomponents/People/Teams/teamEvents";
import {
  archiveTeam,
  fetchTeamById,
  clearTeamDetail,
} from "../../../../../store/features/teamsApi";
import { TeamDetailPageSkeleton } from "../../../../../components/dashboardcomponents/People/Teams/TeamLoadingSkeletons";

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const { detail, detailLoading } = useSelector((state) => state.peopleTeams);

  useEffect(() => {
    if (teamId) dispatch(fetchTeamById({ teamId }));
    return () => dispatch(clearTeamDetail());
  }, [teamId, dispatch]);

  const team = useMemo(
    () => (detail?.id === teamId ? buildTeamDetailView(detail) : null),
    [detail, teamId]
  );

  useEffect(() => {
    const onArchive = () => setArchiveOpen(true);
    const onEdit = () => navigate(`/people/teams/${teamId}/edit`);
    window.addEventListener(TEAM_DETAIL_ARCHIVE, onArchive);
    window.addEventListener(TEAM_DETAIL_EDIT, onEdit);
    return () => {
      window.removeEventListener(TEAM_DETAIL_ARCHIVE, onArchive);
      window.removeEventListener(TEAM_DETAIL_EDIT, onEdit);
    };
  }, [navigate, teamId]);

  if (detailLoading && !team) {
    return <TeamDetailPageSkeleton />;
  }

  if (!team) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>Team not found.</Typography>
      </Box>
    );
  }

  const handleArchiveConfirm = async () => {
    const result = await dispatch(archiveTeam({ teamId }));
    if (archiveTeam.fulfilled.match(result)) {
      toast.success(result.payload?.message || `${team.name} archived`);
      setArchiveOpen(false);
      navigate("/people/teams");
    } else {
      toast.error(result.payload?.message || "Failed to archive team");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <TeamDetailHeaderBand team={team} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pt: "32px",
          px: 3,
          pb: 3,
        }}
      >
        <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
          <TeamDetailStatsRow stats={team.stats} />
          <TeamDetailInfoSection team={team} />
          <TeamDetailGroupsSection groups={team.groups} />
          <TeamDetailChannelsSection channels={team.channels} />
          <TeamDetailMembersSection members={team.members} />
        </Box>
      </Box>

      <TeamArchiveModal
        open={archiveOpen}
        teamName={team.name}
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchiveConfirm}
      />
    </Box>
  );
}
